import Skeleton from '@material-ui/lab/Skeleton';
import React, { JSX, useEffect, useState } from 'react';

import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { FilterPillKey } from '~/src/dash/components/filters/common';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { ModelSecondaryButton } from '~/src/dash/components/model-secondary-button';
import {
  ButtonV2,
  Col,
  Cols,
  fontSizing,
  IndependentSelect,
  Row,
  Rows,
  spacing,
  spacingV2,
  Text,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { LoadingStateLoading } from '~/src/dash/utils/loading-states';
import { ICustomCreativeReviewAPIFilterConfig } from '~/src/dash/views/brand-safety/creative-review/config';
import { ct } from '~/src/dash/publicaUI';

interface IFilterRowProps {
  cypressParentName: string;
  filtersConfig: ICustomCreativeReviewAPIFilterConfig;
  selectedFilterOptions: Record<string, Set<string | number>>;
  handleClearFilters: () => void; // clear all filters function
  handleApplyFilters: () => void; // clear all filters function
  handleFilterAccept: (value: Set<number | string>, key: string) => void;
  options: Record<string, ISelectOption[]>;
  loadState: number;
}

export const CreativeReviewFilters = ({
  filtersConfig,
  selectedFilterOptions,
  handleClearFilters,
  handleFilterAccept,
  handleApplyFilters,
  options,
  loadState,
}: IFilterRowProps): JSX.Element => {
  const filterKeys = Object.keys(filtersConfig) as FilterPillKey[];
  const [reactKey, setReactKey] = useState(0);

  useEffect(() => {
    setReactKey((value) => value + 1);
  }, [selectedFilterOptions]);

  const statusFilter = filtersConfig.Status;
  return (
    <Rows>
      <Row noPadding style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}>
        <Cols max largeHorizontalPadding style={{ position: 'relative' }}>
          <Col style={{ minWidth: '104px' }}>
            <Cols
              gap='8px'
              style={{
                position: 'relative',
                alignItems: 'center',
                height: '42px',
              }}
            >
              <Col paddingTop='2px'>
                <IconV2 name='funnel' color={publicaColors.black} large />
              </Col>
              <Col>
                <Text small bold tKey='ViewBy' />
              </Col>
            </Cols>
          </Col>
          <Col flex>
            <Cols
              key={reactKey}
              wrap
              style={{
                alignItems: 'center',
                height: '100%',
                padding: '5px 8px 5px 0px',
                columnGap: '2px',
                rowGap: spacing.extraSmall,
              }}
            >
              {loadState === LoadingStateLoading && (
                <Skeleton variant='text' animation='wave' width='100%' />
              )}

              {loadState !== LoadingStateLoading && (
                <Col>
                  <IndependentSelect
                    isCheckboxHidden={false}
                    key='Status'
                    handleApplySelect={handleFilterAccept}
                    listLabel='Review Status'
                    options={options[statusFilter.key]}
                    placeholder={statusFilter.placeholder}
                    selectedOptionIDs={selectedFilterOptions.Status}
                    singularLabel={statusFilter.singularLabel}
                    maxWidth={240}
                    onLoadDisplayActiveOnly={statusFilter.onLoadDisplayActiveOnly}
                    isDisabled={statusFilter.isDisabled}
                    disableSort={statusFilter.disableSort}
                    selectedKey={statusFilter.key}
                    selectedLimit={statusFilter.selectedLimit}
                    theme='pill'
                    minWidth={50}
                    iconName={statusFilter.iconName}
                    displayID={statusFilter.addIDPrefix}
                    isSelectionRequired={true}
                  />
                </Col>
              )}
            </Cols>
          </Col>
        </Cols>
      </Row>
      <Row noPadding>
        <Cols max largeHorizontalPadding style={{ position: 'relative' }}>
          <Col style={{ minWidth: '104px' }}>
            <Cols
              gap='8px'
              style={{
                position: 'relative',
                alignItems: 'center',
                height: '42px',
              }}
            >
              <Col paddingTop='2px'>
                <IconV2 name='filter' small />
              </Col>
              <Col>
                <Text small bold tKey='Filters' />
              </Col>
            </Cols>
          </Col>
          <Col flex>
            <Cols
              key={reactKey}
              wrap
              style={{
                alignItems: 'center',
                height: '100%',
                padding: '5px 8px 5px 0px',
                columnGap: '2px',
                rowGap: spacing.extraSmall,
              }}
            >
              {loadState === LoadingStateLoading && (
                <Skeleton variant='text' animation='wave' width='100%' />
              )}
              {loadState !== LoadingStateLoading &&
                filterKeys.map((filterKey) => {
                  if (options[filterKey] !== undefined && filterKey !== 'Status') {
                    const filter = filtersConfig[filterKey];
                    return (
                      <Col>
                        <IndependentSelect
                          isCheckboxHidden={false}
                          key={filterKey}
                          handleApplySelect={handleFilterAccept}
                          listLabel={filter.listLabel}
                          options={options[filter.key]}
                          placeholder={filter.placeholder}
                          selectedOptionIDs={selectedFilterOptions[filterKey]}
                          singularLabel={filter.singularLabel}
                          maxWidth={240}
                          onLoadDisplayActiveOnly={filter.onLoadDisplayActiveOnly}
                          isDisabled={filter.isDisabled}
                          disableSort={filter.disableSort}
                          selectedKey={filter.key}
                          selectedLimit={filter.selectedLimit}
                          theme='pill'
                          minWidth={50}
                          iconName={filter.iconName}
                          displayID={filter.addIDPrefix}
                        />
                      </Col>
                    );
                  } else {
                    return <Col></Col>;
                  }
                })}
              <Col smallPadding>
                <Cols style={{ alignItems: 'center' }} gap={spacing.medium}>
                  <Col>
                    {/* APPLY */}
                    <ButtonV2
                      onClick={handleApplyFilters}
                      style={{
                        padding: `${spacingV2.extraSmall} ${spacingV2.small}`,
                      }}
                    >
                      <Col>
                        <Text sizeV2 small bold color={publicaColors.white} tKey='Apply' />
                      </Col>
                    </ButtonV2>
                  </Col>
                  {/* CLEAR ALL */}
                  <Col>
                    <ModelSecondaryButton
                      variant='text'
                      size='small'
                      onClick={handleClearFilters}
                      style={{ textTransform: 'none', fontSize: fontSizing.small }}
                    >
                      {ct('Clear')}
                    </ModelSecondaryButton>
                  </Col>
                </Cols>
              </Col>
            </Cols>
          </Col>
        </Cols>
      </Row>
    </Rows>
  );
};
